import { useState } from 'react';
import { useStore } from 'react-redux';
import { useAppSelector } from '@/app/hooks';
import { loyaltyApi, useGetLoyaltyProgramQuery, type LoyaltyProgram } from './loyaltyApi';

// SC-1091 — reminding a guest that logging in earns points. All of it stays silent
// when the customer is logged in, the program is off, or the call fails.

/**
 * True when this store carries the loyalty API. The real app always does; the
 * cart-only stores of many tests do not, and a query hook on a store without its
 * reducer throws. The nudge components sit behind this gate so the hook is only
 * ever called where it can work.
 */
export function useLoyaltyApiPresent(): boolean {
  const store = useStore();
  return loyaltyApi.reducerPath in (store.getState() as Record<string, unknown>);
}

/**
 * The public program (`GET /loyalty/program/`, no token) — only for a guest, never
 * on a table order, and `undefined` while unknown or when the call fails.
 */
export function useGuestProgram(restaurantUuid: string, skip = false): LoyaltyProgram | undefined {
  // Optional chaining: a guest is simply « not authenticated » (see CartDrawer).
  const isAuthed = useAppSelector((s) => s.user?.status === 'authenticated');
  const { data } = useGetLoyaltyProgramQuery(restaurantUuid, { skip: skip || isAuthed || !restaurantUuid });
  return isAuthed ? undefined : data;
}

/** A « dismissed for this session » flag (sessionStorage: gone with the tab). */
export function useSessionFlag(key: string): [boolean, () => void] {
  const [on, setOn] = useState(() => {
    try {
      return sessionStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const set = () => {
    setOn(true);
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      /* storage unavailable: dismissed for this page view only */
    }
  };
  return [on, set];
}
